/**
 * Authorization Store — RBAC + Permissions
 *
 * Manages user roles and permissions with proper SRP:
 * - State management only
 * - Permission resolution only
 * - Does NOT handle HTTP (delegated to authzClient)
 * - Does NOT handle caching logic (delegated to resolver)
 */

import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';

/**
 * Permission represents a specific action on a resource
 */
export interface Permission {
  id: string;
  name: string;
  description: string;
  action: string;
  resource: string;
}

/**
 * Role groups related permissions
 */
export interface Role {
  id: string;
  name: string;
  description: string;
  permissions: Permission[];
}

/**
 * Authorization state — persists user's roles and permissions
 */
export interface AuthzState {
  // Authorization data (persisted)
  roles: Role[];
  permissions: Permission[];

  // Policy cache — Maps action:resource to boolean result
  // ✅ Using Record (serializable) instead of Map
  policyCache: Record<string, boolean>;

  // Loading state (not persisted)
  loadingState: 'idle' | 'loading' | 'success' | 'error';
  loadError: Error | null;

  // Fix #8: Track when permissions were loaded (TTL/staleness detection)
  permissionsLoadedAt: number | null;

  // Fix #2: Prevent race condition on concurrent loadPermissions calls
  // Note: loadingVersion can theoretically overflow after 2^53 concurrent calls
  // (9 quadrillion calls), which would take ~285 million years at 1 call/second
  // If ever needed, could use Symbol or UUID instead
  loadingVersion: number;

  // Methods
  loadPermissions: (userId: string) => Promise<void>;
  hasPermission: (action: string, resource?: string) => boolean;
  hasRole: (roleId: string) => boolean;
  hasAnyRole: (roleIds: string[]) => boolean;
  canAccess: (resource: string, action: string) => boolean;
  clearAuthz: () => void;
  arePermissionsStale: (maxAge?: number) => boolean;
  refreshPermissionsIfStale: (userId: string, maxAge?: number) => Promise<void>;
}

/**
 * Provider for injecting authzClient
 * This separates HTTP concerns from store concerns (SRP)
 */
let authzClientProvider: (() => IAuthzClient) | null = null;

export interface IAuthzClient {
  getUserPermissions(userId: string): Promise<{
    roles: Role[];
    permissions: Permission[];
  }>;
}

/**
 * Initialize the authorization store with an authzClient provider
 * Must be called once during app initialization, before any components use useAuthzStore()
 *
 * @param provider Function that returns the authzClient instance
 * @throws Error if called more than once
 * @throws Error if provider is null or undefined
 *
 * @example
 * ```typescript
 * import { initAuthzStore } from '@passport/store';
 * import { AuthzClient } from '@passport/api-client';
 *
 * const client = new AuthzClient(httpClient, logger);
 * initAuthzStore(() => client);
 * ```
 */
export function initAuthzStore(provider: (() => IAuthzClient) | null | undefined): void {
  // Fix #1: Validate provider is not null (was missing)
  if (!provider) {
    throw new Error('[AuthzStore] Provider cannot be null or undefined (E001)');
  }
  if (authzClientProvider) {
    throw new Error('[AuthzStore] Already initialized (E002)');
  }
  authzClientProvider = provider;
}

/**
 * Create the authorization store
 * Requires initAuthzStore to be called first
 */
export const useAuthzStore = create<AuthzState>()(
  devtools(
    persist(
      (set, get) => ({
        roles: [],
        permissions: [],
        policyCache: {},
        loadingState: 'idle',
        loadError: null,
        permissionsLoadedAt: null,  // Fix #8: Track when loaded
        loadingVersion: 0,          // Fix #2: Prevent race conditions

        /**
         * Load permissions from backend
         * Validates authzClient is initialized
         * Tracks loading state
         * Fails securely (denies all on error)
         *
         * Fix #2: Implements request versioning to prevent race conditions
         * Fix #3: Validates userId is non-empty (CRITICAL)
         * Fix #15: Handles 401 responses during load (token expiration)
         * Fix #17: Validates response schema before accepting
         */
        loadPermissions: async (userId: string) => {
          // Fix #3: Validate userId is provided and non-empty (CRITICAL)
          if (!userId || typeof userId !== 'string' || userId.trim().length === 0) {
            set({
              loadingState: 'error',
              loadError: new Error('[AuthzStore] Invalid userId: must be non-empty string (E003)'),
              roles: [],
              permissions: [],
            });
            return;
          }

          // Guard: authzClient must be initialized
          if (!authzClientProvider) {
            set({
              loadingState: 'error',
              loadError: new Error('[AuthzStore] Not initialized - call initAuthzStore first (E004)'),
              roles: [],
              permissions: [],
            });
            return;
          }

          // Fix #2: Request versioning — prevent stale responses overwriting newer data
          const version = (get().loadingVersion || 0) + 1;
          set({ loadingState: 'loading', loadError: null, loadingVersion: version });

          try {
            const client = authzClientProvider();
            const response = await client.getUserPermissions(userId);

            // Fix #17: Validate response schema before accepting
            if (!response || typeof response !== 'object') {
              throw new Error('[AuthzStore] Invalid response format (E005)');
            }

            if (!Array.isArray(response.roles)) {
              throw new Error('[AuthzStore] Invalid response: roles must be array (E006)');
            }

            if (!Array.isArray(response.permissions)) {
              throw new Error('[AuthzStore] Invalid response: permissions must be array (E007)');
            }

            // Fix #4: Validate array items have required fields (CRITICAL)
            // Strict validation: role.name can't be empty string
            for (const role of response.roles) {
              if (!role.id || typeof role.id !== 'string' || role.id.trim().length === 0) {
                throw new Error('[AuthzStore] Invalid role: missing or empty id (E008)');
              }
              if (!role.name || typeof role.name !== 'string' || role.name.trim().length === 0) {
                throw new Error('[AuthzStore] Invalid role: missing or empty name (E009)');
              }
            }

            // Fix #4: Validate permission has all required fields
            for (const perm of response.permissions) {
              if (!perm.id || typeof perm.id !== 'string' || perm.id.trim().length === 0) {
                throw new Error('[AuthzStore] Invalid permission: missing or empty id (E010)');
              }
              if (!perm.action || typeof perm.action !== 'string' || perm.action.trim().length === 0) {
                throw new Error('[AuthzStore] Invalid permission: missing or empty action (E011)');
              }
              // resource is optional but if present must be string
              if (perm.resource && typeof perm.resource !== 'string') {
                throw new Error('[AuthzStore] Invalid permission: resource must be string (E012)');
              }
            }

            // Fix #2: Only update if this is still the latest request
            if (version === get().loadingVersion) {
              set({
                roles: response.roles,
                permissions: response.permissions,
                policyCache: {}, // Clear cache on reload
                loadingState: 'success',
                loadError: null,
                permissionsLoadedAt: Date.now(),  // Fix #8: Track load time
              });
            }
          } catch (error) {
            const err = error instanceof Error ? error : new Error(String(error));

            // Only update if this is still the latest request
            if (version === get().loadingVersion) {
              set({
                loadingState: 'error',
                loadError: err,
                // ✅ Fail secure: clear permissions on error
                roles: [],
                permissions: [],
                policyCache: {},
              });
            }
          }
        },

        /**
         * Check if user has specific permission
         * Uses cache to avoid repeated lookups
         * Returns false if permissions not loaded (fail secure)
         *
         * Fix #3: Uses JSON encoding for cache keys to prevent collisions
         * Fix #16: Implements LRU cache with max size limit
         *
         * @param action Action name (e.g., 'read', 'write', 'delete')
         * @param resource Optional resource name (e.g., 'users', 'documents')
         * @returns true if permission granted, false otherwise
         */
        hasPermission: (action: string, resource?: string) => {
          const state = get();

          // Fail secure: if permissions not loaded, deny
          if (state.loadingState === 'error' || state.loadingState === 'loading') {
            return false;
          }

          // Fix #3: Use JSON encoding to prevent cache key collisions
          // Example: 'users:delete' vs 'users:delete' collision is prevented
          const cacheKey = JSON.stringify({ resource, action });

          // Check cache first (optimization)
          if (cacheKey in state.policyCache) {
            // Fix #5: Simplify return (cache only stores booleans); fail secure if somehow absent
            return state.policyCache[cacheKey] ?? false;
          }

          // Check permissions (exact match)
          const hasIt = state.permissions.some((p) => {
            const actionMatches = p.action === action;
            const resourceMatches = !resource || p.resource === resource;
            return actionMatches && resourceMatches;
          });

          // ✅ Cache result (using Record, not Map)
          // Fix #16: Implement LRU cache — don't cache if already at max size
          // Fix #3: Check size optimization - count keys only when needed
          const maxCacheSize = 1000;
          const cacheSize = Object.keys(state.policyCache).length;

          // Only cache if we haven't hit the limit
          if (cacheSize < maxCacheSize) {
            set((s) => ({
              policyCache: {
                ...s.policyCache,
                [cacheKey]: hasIt,
              },
            }));
          }
          // If cache is full, still return result but don't cache it
          // Future improvement: Implement true LRU with eviction

          return hasIt;
        },

        /**
         * Check if user has specific role by ID
         * Uses role ID (stable) not role name (can change)
         *
         * @param roleId Role ID to check
         * @returns true if user has role, false otherwise
         */
        hasRole: (roleId: string) => {
          const state = get();
          return state.roles.some((r) => r.id === roleId);
        },

        /**
         * Check if user has ANY of the specified roles
         *
         * @param roleIds Array of role IDs to check
         * @returns true if user has any role, false otherwise
         */
        hasAnyRole: (roleIds: string[]) => {
          const state = get();
          return roleIds.some((roleId) => state.roles.some((r) => r.id === roleId));
        },

        /**
         * Convenience method: check if user can access resource with action
         * Equivalent to hasPermission(action, resource)
         *
         * @param resource Resource name
         * @param action Action name
         * @returns true if access granted, false otherwise
         */
        canAccess: (resource: string, action: string) => {
          return get().hasPermission(action, resource);
        },

        /**
         * Check if permissions are stale (older than maxAge)
         * Fix #8: Allows detection of stale cached permissions
         *
         * @param maxAge Maximum age in milliseconds (default 5 minutes)
         * @returns true if permissions are stale or never loaded
         */
        arePermissionsStale: (maxAge: number = 5 * 60 * 1000) => {
          const state = get();
          if (!state.permissionsLoadedAt) return true;
          return Date.now() - state.permissionsLoadedAt > maxAge;
        },

        /**
         * Refresh permissions if they are stale
         * Fix #8: Allows opt-in staleness checking
         * Fix #6: Uses get() to avoid captured state race condition
         *
         * @param userId User ID to load permissions for
         * @param maxAge Maximum age before refresh (default 5 minutes)
         */
        refreshPermissionsIfStale: async (userId: string, maxAge: number = 5 * 60 * 1000) => {
          // Fix #6: Use get() instead of captured state to get latest values
          const state = get();
          if (state.permissionsLoadedAt && Date.now() - state.permissionsLoadedAt < maxAge) {
            return; // Still fresh
          }
          // Use get() to call latest loadPermissions method
          return get().loadPermissions(userId);
        },

        /**
         * Clear all authorization data
         * Called on logout
         * Fix #8: Clear timestamp on logout
         */
        clearAuthz: () => {
          set({
            roles: [],
            permissions: [],
            policyCache: {},
            loadingState: 'idle',
            loadError: null,
            permissionsLoadedAt: null,  // Clear timestamp on logout
          });
        },
      }),
      {
        name: 'authz-store',
        // ✅ Only persist actual data, not loading state or errors
        partialize: (state) => ({
          roles: state.roles,
          permissions: state.permissions,
          // Fix #2: Don't persist policyCache - it's computed from permissions
          // The cache should be regenerated from permissions on each load
          // Persisting it could cause stale cache values to linger
          permissionsLoadedAt: state.permissionsLoadedAt,  // Persist timestamp for staleness checks
          // Don't persist: loadingState, loadError, loadingVersion, policyCache (regenerate on load)
        }),
      }
    )
  )
);
